/**
 * T223.D — Customer refund submission page.
 *
 * Route: /account/billing/refunds/
 *
 * Lists the customer's own refund history (status, amount, timeline)
 * and provides a form to submit a new request. Manual-approval flow:
 * once submitted the row sits at REQUESTED until Manivannan reviews;
 * the timeline shows each transition (RefundEvent rows from the
 * backend, filtered to customer-visible fields).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Send, CheckCircle2, XCircle, Clock, AlertCircle } from "lucide-react";

import { listRefunds, createRefund } from "../../api/refunds.api";
import useSubscriptionStore from "../../store/subscription.store";


const REASON_CODES = [
  { value: "DUPLICATE_CHARGE",      label: "Duplicate charge" },
  { value: "SERVICE_NOT_DELIVERED", label: "Service not delivered" },
  { value: "DOWNGRADE_PRORATA",     label: "Downgrade pro-rata" },
  { value: "GOODWILL",              label: "Goodwill / one-off issue" },
  { value: "OTHER",                 label: "Other (explain below)" },
];

const STATUS_PILL = {
  REQUESTED:  { label: "Awaiting review", className: "bg-yellow-100 text-yellow-800", icon: Clock },
  APPROVED:   { label: "Approved",        className: "bg-blue-100 text-blue-800",   icon: CheckCircle2 },
  DENIED:     { label: "Denied",          className: "bg-gray-100 text-gray-700",   icon: XCircle },
  PROCESSING: { label: "Processing",      className: "bg-purple-100 text-purple-800", icon: Clock },
  COMPLETED:  { label: "Completed",       className: "bg-green-100 text-green-800",   icon: CheckCircle2 },
  FAILED:     { label: "Failed",          className: "bg-red-100 text-red-800",       icon: AlertCircle },
};


function StatusPill({ status }) {
  const cfg = STATUS_PILL[status] || { label: status, className: "bg-gray-100 text-gray-700", icon: Clock };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cfg.className}`}>
      <Icon className="w-3.5 h-3.5" />
      {cfg.label}
    </span>
  );
}


function RefundCard({ refund }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border rounded-lg p-4 bg-white">
      <div className="flex justify-between items-start mb-2">
        <div>
          <div className="text-lg font-semibold">₹{refund.amount_inr}</div>
          <div className="text-sm text-gray-600">{refund.reason_code.replace(/_/g, " ")}</div>
        </div>
        <StatusPill status={refund.status} />
      </div>
      {refund.customer_facing_reason && (
        <p className="text-sm text-gray-700 mb-2 whitespace-pre-wrap">{refund.customer_facing_reason}</p>
      )}
      <div className="text-xs text-gray-500">
        Submitted {new Date(refund.created_at).toLocaleDateString()}
        {refund.completed_at && ` · Completed ${new Date(refund.completed_at).toLocaleDateString()}`}
      </div>
      {refund.timeline && refund.timeline.length > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-2 text-xs text-blue-600 hover:underline"
        >
          {expanded ? "Hide" : "Show"} status timeline ({refund.timeline.length})
        </button>
      )}
      {expanded && (
        <ol className="mt-2 border-l-2 border-gray-200 pl-3 space-y-1">
          {refund.timeline.map((ev) => (
            <li key={ev.id} className="text-xs">
              <div className="font-medium">{ev.label}</div>
              <div className="text-gray-500">{new Date(ev.when).toLocaleString()}</div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}


export default function Refunds() {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const subs = useSubscriptionStore((s) => s.list);
  const fetchSubs = useSubscriptionStore((s) => s.fetch);

  // Form state
  const [subId, setSubId] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("OTHER");
  const [explanation, setExplanation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await listRefunds();
      setRefunds(data.refunds || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);
  useEffect(() => { if (typeof fetchSubs === "function") fetchSubs(); }, [fetchSubs]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!subId || !amount || !explanation.trim()) {
      setError("All fields are required.");
      return;
    }
    const amt = parseFloat(amount);
    if (!(amt > 0)) {
      setError("Amount must be greater than zero.");
      return;
    }
    setSubmitting(true);
    try {
      await createRefund({
        subscriptionId: subId,
        amountInr: amt,
        reasonCode: reason,
        customerFacingReason: explanation.trim(),
      });
      setShowForm(false);
      setSubId(""); setAmount(""); setReason("OTHER"); setExplanation("");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail
        || err?.response?.data?.subscription_id?.[0]
        || err?.response?.data?.amount_inr?.[0]
        || "Failed to submit refund. Please try again.";
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto p-4">
      <Link to="/subscriptions" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3">
        <ChevronLeft className="w-4 h-4" /> Back to subscriptions
      </Link>

      <div className="flex justify-between items-start mb-4">
        <div>
          <h1 className="text-2xl font-bold">Refunds</h1>
          <p className="text-sm text-gray-600">
            Request a refund on a past charge. Every request is reviewed manually —
            you'll see status updates here.
          </p>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
          >
            Request a refund
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="border rounded-lg p-4 bg-gray-50 mb-4 space-y-3">
          <h2 className="font-semibold">New refund request</h2>

          <label className="block">
            <span className="text-sm font-medium">Subscription</span>
            <select
              value={subId}
              onChange={(e) => setSubId(e.target.value)}
              required
              className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
            >
              <option value="">— select —</option>
              {(subs || []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.plan?.name || s.plan_code} ({s.status})
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-sm font-medium">Amount (₹)</span>
            <input
              type="number"
              step="0.01"
              min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium">Reason</span>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
            >
              {REASON_CODES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-sm font-medium">Explain in your own words</span>
            <textarea
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              required
              rows={4}
              maxLength={2000}
              className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
              placeholder="What happened? Anything that helps us evaluate the refund."
            />
            <span className="text-xs text-gray-500">{explanation.length}/2000</span>
          </label>

          {error && (
            <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              {error}
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              <Send className="w-4 h-4" />
              {submitting ? "Submitting…" : "Submit request"}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setError(""); }}
              className="px-3 py-2 border rounded text-sm hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <h2 className="font-semibold mb-2">Past requests</h2>
      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : refunds.length === 0 ? (
        <p className="text-sm text-gray-500">No refund requests yet.</p>
      ) : (
        <div className="space-y-3">
          {refunds.map((r) => <RefundCard key={r.id} refund={r} />)}
        </div>
      )}
    </div>
  );
}
